import React from 'react';
import './ReferralPage.css';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  CopyOutlined,
  DollarOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  GiftOutlined,
} from '@ant-design/icons';

import { useAuth } from '../../hooks/useAuth';
import { loadReferralSummary } from './reducer';
import { selectReferralLoading, selectReferralSummary } from './selectors';
import Flex from '../../components/Flex/Flex';
import Text from '../../components/Text/Text';
import Button from '../../components/Button/Button';
import Table from '../../components/Table/Table';

const ReferralPage = React.memo(() => {
  const { t } = useTranslation('translation');
  const { user } = useAuth();
  const dispatch = useDispatch();

  const summary = useSelector(selectReferralSummary);
  const loading = useSelector(selectReferralLoading);

  React.useEffect(() => {
    if (user) {
      dispatch(loadReferralSummary());
    }
  }, [dispatch, user]);

  const handleCopy = React.useCallback(
    (value: string) => {
      if (!value) {
        return;
      }
      navigator.clipboard
        .writeText(value)
        .then(() => toast.success(t('referral_copied')))
        .catch(() => toast.error(t('referral_copy_failed')));
    },
    [t],
  );

  if (!user) {
    return null;
  }

  const stats = [
    {
      icon: <DollarOutlined />,
      accent: 'SurfaceCard__icon--green',
      label: t('referral_balance'),
      value: summary ? `$${summary.balance.toFixed(2)}` : '—',
    },
    {
      icon: <TeamOutlined />,
      accent: 'SurfaceCard__icon--blue',
      label: t('referral_total'),
      value: summary ? String(summary.totalReferrals) : '—',
    },
    {
      icon: <CheckCircleOutlined />,
      accent: 'SurfaceCard__icon--gold',
      label: t('referral_active'),
      value: summary ? String(summary.activeSubscriptions) : '—',
    },
  ];

  return (
    <div className="PageShell ReferralPage">
      <header className="PageHero">
        <div className="PageHero__glow" aria-hidden />
        <Flex vertical gap={12} className="PageHero__content">
          <span className="PageHero__eyebrow">
            <GiftOutlined /> Fish Hunter
          </span>
          <Text size={6} bold>
            {t('referral_title')}
          </Text>
          <Text type="secondary" size={3}>
            {t('referral_subtitle')}
          </Text>
        </Flex>
      </header>

      <section className="ReferralPage__stats">
        {stats.map((stat) => (
          <div key={stat.label} className="SurfaceCard SurfaceCard--compact ReferralPage__stat">
            <span className={`SurfaceCard__icon ${stat.accent}`}>{stat.icon}</span>
            <Flex vertical gap={4}>
              <Text type="secondary" size={2}>
                {stat.label}
              </Text>
              <Text size={5} bold>
                {stat.value}
              </Text>
            </Flex>
          </div>
        ))}
      </section>

      <section className="SurfaceCard ReferralPage__share">
        <Text size={4} bold>
          {t('referral_your_link')}
        </Text>
        <Text type="secondary" size={2}>
          {t('referral_share_hint')}
        </Text>

        <Flex vertical gap={12} className="ReferralPage__share-fields">
          <div className="ReferralPage__field">
            <Flex vertical gap={4} flex>
              <Text type="tertiary" size={1} bold>
                {t('referral_code')}
              </Text>
              <Text size={4} bold>
                {summary?.referralCode ?? '—'}
              </Text>
            </Flex>
            <Button type="default" onClick={() => handleCopy(summary?.referralCode ?? '')}>
              <CopyOutlined /> {t('referral_copy')}
            </Button>
          </div>

          <div className="ReferralPage__field">
            <Flex vertical gap={4} flex>
              <Text type="tertiary" size={1} bold>
                {t('referral_link')}
              </Text>
              <Text size={2} className="ReferralPage__link-text">
                {summary?.referralLink ?? '—'}
              </Text>
            </Flex>
            <Button type="primary" onClick={() => handleCopy(summary?.referralLink ?? '')}>
              <CopyOutlined /> {t('referral_copy')}
            </Button>
          </div>
        </Flex>
      </section>

      <section className="PageSection">
        <div className="PageSection__head">
          <Text size={5} bold>
            {t('referral_my_referrals')}
          </Text>
          <Text type="secondary" size={2}>
            {t('referral_my_referrals_hint')}
          </Text>
        </div>
        {summary && summary.referrals.length > 0 ? (
          <Table
            columns={[
              {
                title: t('referral_user'),
                dataIndex: 'email',
              },
              {
                title: t('referral_status'),
                dataIndex: 'hasSubscription',
                render: (x) =>
                  x.hasSubscription ? (
                    <Text type="success">{t('referral_status_active')}</Text>
                  ) : x.isConfirmed ? (
                    <Text type="secondary">{t('referral_status_no_sub')}</Text>
                  ) : (
                    <Text type="tertiary">{t('referral_status_pending')}</Text>
                  ),
              },
              {
                title: t('referral_sub_until'),
                dataIndex: 'subscriptionDate',
                render: (x) =>
                  x.subscriptionDate ? (
                    <Text>{dayjs(x.subscriptionDate).format('YYYY-MM-DD')}</Text>
                  ) : (
                    <Text type="tertiary">—</Text>
                  ),
              },
            ]}
            data={summary.referrals}
            pagination={false}
          />
        ) : (
          <Text type="secondary">{loading ? t('referral_loading') : t('referral_empty')}</Text>
        )}
      </section>

      <section className="PageSection">
        <div className="PageSection__head">
          <Text size={5} bold>
            {t('referral_history')}
          </Text>
          <Text type="secondary" size={2}>
            {t('referral_history_hint')}
          </Text>
        </div>
        {summary && summary.history.length > 0 ? (
          <Table
            columns={[
              {
                title: t('referral_date'),
                dataIndex: 'createdAt',
                render: (x) => <Text>{dayjs(x.createdAt).format('YYYY-MM-DD')}</Text>,
              },
              {
                title: t('referral_user'),
                dataIndex: 'payerEmail',
              },
              {
                title: t('referral_payment'),
                dataIndex: 'amount',
                render: (x) => <Text>${Number(x.amount).toFixed(2)}</Text>,
              },
              {
                title: t('referral_percent'),
                dataIndex: 'commissionPercent',
                render: (x) => <Text>{Number(x.commissionPercent)}%</Text>,
              },
              {
                title: t('referral_earned'),
                dataIndex: 'commissionAmount',
                render: (x) => <Text type="success">${Number(x.commissionAmount).toFixed(2)}</Text>,
              },
            ]}
            data={summary.history}
            pagination={false}
          />
        ) : (
          <Text type="secondary">
            {loading ? t('referral_loading') : t('referral_history_empty')}
          </Text>
        )}
      </section>

      <aside className="SurfaceNote ReferralPage__note">
        <Flex vertical gap={8}>
          <Text type="secondary" size={2}>
            {t('referral_note')}
          </Text>
          <Text type="secondary" size={2}>
            {t('referral_payout')}
          </Text>
        </Flex>
      </aside>
    </div>
  );
});

export default ReferralPage;
